import { useRef } from 'react'
import { ArrowRight } from 'lucide-react'
import { AddStrategyRow } from '@/components/custom/ogsm/add-strategy-row'
import { SiblingList } from '@/components/custom/ogsm/board-reorder'
import { DragHandle } from '@/components/custom/ogsm/drag-handle'
import { HiddenStrategiesChip } from '@/components/custom/ogsm/inactive-toggle'
import { NodeHeading } from '@/components/custom/ogsm/node-heading'
import { useBoardEdit } from '@/features/ogsm/board-edit-context'
import { useFocusFallback } from '@/hooks/use-focus-fallback'
import { visibleNodes } from '@/features/ogsm/inactive'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'
import { STATUS_BORDER } from '@/features/ogsm/status-styles'
import { StrategyProgress } from '@/components/custom/ogsm/strategy-progress'
import { StrategyRow } from '@/components/custom/ogsm/strategy-row'
import { TargetCell } from '@/components/custom/ogsm/target-cell'
import { useOgsmShowInactive } from '@/hooks/use-ogsm-show-inactive'
import { useOgsmSortable } from '@/hooks/use-ogsm-sortable'
import { cn } from '@/lib/utils'
import type { OgsmBoardNode } from '@/types/api'

export const BOARD_COLUMNS =
  'lg:grid-cols-[2.5rem_minmax(0,2fr)_minmax(9.5rem,1.6fr)_minmax(0,2fr)_14rem_2.5rem]'

interface ObjectiveBandProps {
  node: OgsmBoardNode
  published: boolean
  onDrill?: (nodeId: string) => void
}

/**
 * One objective with its goal and, to the right, its strategies and measures.
 * In a drilled view, each child of the opened node is rendered here as the objective.
 */
export function ObjectiveBand({
  node,
  published,
  onDrill,
}: ObjectiveBandProps) {
  const { session } = useBoardEdit()
  const editable = session.structureEditable
  const { setNodeRef, style, isDragging, handle } = useOgsmSortable(
    node.id,
    !editable
  )
  const { showInactive } = useOgsmShowInactive()
  const chip = useRef<HTMLButtonElement>(null)
  const focusFallback = useFocusFallback(chip)
  // Hiding is render-only: drill, reorder positions and counts use the full list.
  const strategies = visibleNodes(node.strategies, showInactive)
  // Children decide, never depth: at the backend's cap a node has none.
  const canOpen = node.strategies.length > 0 && onDrill
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'grid grid-cols-1 border-t border-l-4 border-t-primary/45',
        BOARD_COLUMNS,
        STATUS_BORDER[node.status],
        // Opaque, so a taller node sliding over its siblings hides them.
        isDragging &&
          'relative z-10 bg-background bg-linear-to-b from-primary/5 to-primary/5'
      )}
    >
      {editable ? (
        <div className="flex justify-start px-3 pt-3 lg:justify-center lg:px-0 lg:pt-3.5">
          <DragHandle
            bindings={handle}
            tone="band"
            label={OGSM_COPY.reorder.handleLabel(
              node.title.trim() || OGSM_COPY.untitled
            )}
            hint={
              node.depth === 0
                ? OGSM_COPY.reorder.objectivesHint
                : OGSM_COPY.reorder.strategiesHint
            }
          />
        </div>
      ) : (
        <div className="hidden lg:block" />
      )}

      <div className="min-w-0 bg-primary/3 px-3 py-5">
        <NodeHeading
          node={node}
          titleVariant="h5"
          tag={
            node.includeInScorecard
              ? published
                ? OGSM_COPY.inScorecard
                : OGSM_COPY.flaggedForScorecard
              : null
          }
        />
      </div>

      <div className="min-w-0 border-primary/10 bg-primary/3 px-3 py-5 lg:border-l">
        <TargetCell node={node} />
      </div>

      <div
        className="grid min-w-0 grid-cols-1 border-primary/20 lg:col-span-2 lg:grid-cols-subgrid lg:border-l"
        {...focusFallback}
      >
        <SiblingList ids={strategies.map((strategy) => strategy.id)}>
          {strategies.map((strategy) => (
            <StrategyRow key={strategy.id} node={strategy} />
          ))}
        </SiblingList>
        <AddStrategyRow
          parent={node}
          progress={<StrategyProgress node={node} />}
          inactiveToggle={
            <HiddenStrategiesChip count={node.strategiesInactive} ref={chip} />
          }
        />
      </div>

      {canOpen ? (
        <button
          type="button"
          aria-label={OGSM_COPY.drillDown}
          title={OGSM_COPY.drillDown}
          onClick={() => onDrill(node.id)}
          className="flex cursor-pointer items-center justify-center border-primary/10 bg-status-on-track/10 text-status-drill transition-colors hover:bg-status-on-track/25 focus-visible:ring-2 focus-visible:ring-status-drill/50 focus-visible:outline-none focus-visible:ring-inset lg:border-l"
        >
          <ArrowRight className="size-4" strokeWidth={1.5} />
        </button>
      ) : (
        <div className="border-primary/10 lg:border-l" />
      )}
    </div>
  )
}
